import { defaultAxiosInstance } from '../config/axiosConfig';

/**
 * Knowledge Graph datasets (`/kg-datasets`, permission `knowledge_graph`).
 *
 * Despite the name this is a FACETED SEARCH ENGINE, not a citation network. A
 * dataset is a set of structured records the customer pushes as JSON, with
 * declared `facets` — the fields worth filtering on. The agent gets tools that
 * filter, count and group over them EXACTLY (kg_filter / kg_count / kg_related /
 * kg_describe), plus kg_search which runs semantic search inside a filtered
 * subset. The vector KB stays available for open-ended questions.
 *
 * Lifecycle:
 *   create (draft) → author mapping → push records → activate → attached to agents
 *                          ▲                  │
 *                          └──── dry-run ─────┘
 *
 * A dataset is INVISIBLE to agents until `status === 'active'`. Everything can
 * look correct — records loaded, facets populated, folder attached — and the
 * agent still has no KG tools. Activation is refused while `counts.records === 0`.
 *
 * `KG_ENABLED` is the master switch server-side: when off, every route here 404s.
 * A 404 also means "not found OR not yours" — deliberately indistinguishable, so
 * never render anything implying a dataset exists but belongs to someone else.
 *
 * Auth is the caller's x-api-key, attached by the shared axios interceptor.
 * Errors are already normalised by the response interceptor into an Error
 * carrying `.status` and `.data` — so no try/catch here.
 *
 * ⚠️ ID vs KEY: `pushRecords` takes the dataset **key** (the url-safe string the
 * customer chose). Every other call takes the numeric **id**. This is the easiest
 * thing in the whole API to get wrong.
 */

// The shared instance times out at 30s. Delete is synchronous and scales with
// size — measured at roughly 31 seconds per MILLION records — so a default
// timeout would surface as a failure while the delete is still succeeding.
const DELETE_TIMEOUT_MS = 300000;
// A push commits records and facets synchronously (only the text is queued), so
// a 500-record batch can comfortably outrun 30s on a large payload.
const PUSH_TIMEOUT_MS = 120000;
// Dry-run derives every sample record through the full mapper.
const DRY_RUN_TIMEOUT_MS = 60000;
// Inspect walks up to 200 raw records, decoding embedded JSON as it goes, and
// proposes a mapping off the back of it — more work per record than a dry-run.
const INSPECT_TIMEOUT_MS = 90000;

class KgDatasetService {
  constructor() {
    this.httpClient = defaultAxiosInstance;
  }

  // ── Datasets ─────────────────────────────────────────────────────────────

  /**
   * Lists only datasets the caller OWNS. There is no "shared with me" list —
   * a shared dataset is reachable by id but never appears here, so treat every
   * KG screen as owner-facing.
   */
  async listDatasets(params = {}) {
    const res = await this.httpClient.get('/kg-datasets', { params });
    return res.data;
  }

  /** The only call that returns `mapping_config`. */
  async getDataset(id) {
    const res = await this.httpClient.get(`/kg-datasets/${encodeURIComponent(id)}`);
    return res.data;
  }

  /**
   * Creates the dataset AND its own D3 folder — `folder_id` on the response is
   * what gets attached to an agent.
   *
   * @param {object} body { key, label, mapping_config, description?,
   *   embedding_dimensions?, chunking_overrides? }
   *
   * `mapping_config` is REQUIRED at create. `embedding_dimensions` (default 512)
   * is creation-only and cannot be changed later without re-embedding
   * everything. `chunking_overrides` is an advanced escape hatch whose defaults
   * are tuned for KG records — most UIs should not expose it at all.
   */
  async createDataset(body) {
    const res = await this.httpClient.post('/kg-datasets', body);
    return res.data;
  }

  /** `label` / `description` only — anything else is ignored. */
  async updateDataset(id, body) {
    const res = await this.httpClient.patch(`/kg-datasets/${encodeURIComponent(id)}`, body);
    return res.data;
  }

  /**
   * OWNER ONLY — a write-share is not enough. Removes the records, facets, the
   * folder and the embeddings. Synchronous, and above a few million records it
   * can exceed the database's own 2-minute statement limit regardless of this
   * client timeout.
   */
  async deleteDataset(id) {
    const res = await this.httpClient.delete(`/kg-datasets/${encodeURIComponent(id)}`, {
      timeout: DELETE_TIMEOUT_MS,
    });
    return res.data;
  }

  // ── Inspect ──────────────────────────────────────────────────────────────

  /**
   * Read-only introspection of raw sample records. Needs no dataset, writes
   * nothing, and is the FIRST step of setup — before any mapping exists.
   *
   * This replaces client-side schema sniffing entirely. The server walks the
   * samples, decodes JSON-string columns, flattens nested shapes, and answers
   * with the paths that actually exist plus a proposed mapping. Guessing any of
   * that in the browser now produces paths the server would not agree with.
   *
   * @param {unknown[]} samples 20–200 raw records, ≤2 MB. Push the ORIGINAL
   *   records — no client-side flattening or unwrapping.
   * @param {object} [options]
   * @param {object|null} [options.preprocessing] omit entirely to get a
   *   PROPOSAL back; pass `{}` to mean "decode nothing"; pass a stored config to
   *   inspect through it. Not the same thing as omitting it.
   * @param {string} [options.sourcePath] optional column that says which source
   *   a record came from, for per-source coverage on a mixed export.
   *
   * @returns {Promise<{fields, suggestions, preview, failures, warnings, preprocessing}>}
   *
   * Three rules the caller must honour:
   *   1. The returned `fields[].path` values and the returned `preprocessing`
   *      belong TOGETHER. Derived alias names (`payload_1`) are assigned per
   *      inspection, so paths from one run are not valid under another run's
   *      preprocessing. Store them as a pair.
   *   2. `preview` is TRUNCATED. Never send it back as push input.
   *   3. Coverage describes what was INSPECTED, not the whole dataset — respect
   *      the truncation warnings rather than reporting them as totals.
   */
  async inspect(samples, { preprocessing, sourcePath } = {}) {
    const body = { samples };
    // Deliberately distinguishes "omitted" from "{}": omitted asks the server to
    // propose a preprocessing config, `{}` tells it to decode nothing.
    if (preprocessing !== undefined) body.preprocessing = preprocessing;
    if (sourcePath) body.source_path = sourcePath;
    const res = await this.httpClient.post('/kg-datasets/inspect', body, {
      timeout: INSPECT_TIMEOUT_MS,
    });
    return res.data;
  }

  // ── Mapping ──────────────────────────────────────────────────────────────

  /**
   * Validate a config against sample records. Needs no dataset and WRITES
   * NOTHING, so it is safe to fire on every mapping edit.
   *
   * @returns {Promise<{report, preview}>} `report` carries facet_coverage,
   *   unmapped_fields, warnings and skipped — surface ALL of it, never a
   *   pass/fail. A facet at 0 coverage is mapped to a field that is empty in the
   *   sample, and finding that here rather than after a 10M-record load is most
   *   of the value.
   */
  async dryRun(mappingConfig, samples) {
    const res = await this.httpClient.post(
      '/kg-datasets/dry-run',
      { mapping_config: mappingConfig, samples },
      { timeout: DRY_RUN_TIMEOUT_MS }
    );
    return res.data;
  }

  /**
   * Replace the mapping config. Two hard rules, both 400s:
   *   1. `mapping_config.dataset.key` must equal the dataset's `key`.
   *   2. Once `counts.records > 0`, the set of (text.path, text.embed) pairs
   *      cannot change — text bodies are not retained, they are already chunked
   *      into the vector store, so a change is only expressible as a re-push.
   *
   * Facets, dates, labels, notes and aliases stay freely editable at any time. A
   * successful PUT bumps `mapping_version` and re-syncs the facet catalog — but
   * it does NOT re-derive facets on existing records; only a full re-push does.
   */
  async putMapping(id, mappingConfig) {
    const res = await this.httpClient.put(`/kg-datasets/${encodeURIComponent(id)}/mapping`, {
      mapping_config: mappingConfig,
    });
    return res.data;
  }

  // ── Status ───────────────────────────────────────────────────────────────

  /**
   * @param {'active'|'paused'|'archived'|'draft'} status
   *
   * Activating an empty dataset is refused with a 400: an active empty dataset
   * answers every question with zero, which reads as "the data is missing"
   * rather than "nothing is loaded yet".
   */
  async setStatus(id, status) {
    const res = await this.httpClient.post(`/kg-datasets/${encodeURIComponent(id)}/status`, {
      status,
    });
    return res.data;
  }

  // ── Facets ───────────────────────────────────────────────────────────────

  /**
   * Facet vocabulary + coverage — the same shape the agent's `kg_describe` sees,
   * and a UI spec for the filter controls (one control per facet, driven by
   * `cardinality`).
   *
   * `coverage_pct` must be rendered next to every count derived from a facet.
   * `more_values: true` means the value list is TRUNCATED — never render it as
   * "the values are". Percentages may be up to KG_CATALOG_CACHE_TTL (300s)
   * stale; names and values refresh immediately when records or mapping_version
   * change.
   */
  async getFacets(id, params = {}) {
    const res = await this.httpClient.get(`/kg-datasets/${encodeURIComponent(id)}/facets`, {
      params,
    });
    return res.data;
  }

  // ── Records ──────────────────────────────────────────────────────────────

  /**
   * Two renderings of ONE cohort — filters, dates and ordering mean the same
   * thing in both by construction. Browsing works in EVERY status including
   * `draft`: the status gate stops agents seeing a dataset, not its owner, which
   * is what makes "check it looks right before you activate" a real step.
   *
   * @param {object} params
   *   view        'table' (default) | 'graph'
   *   filters     JSON string (URL-encoded). Values within one facet OR,
   *               separate facets AND. Send facet values VERBATIM — they are
   *               matched on their normalised form.
   *   date_from / date_to   `date_to` is EXCLUSIVE
   *   order_by    'date_desc' (default) | 'date_asc' | 'title' | 'recent'
   *   limit / offset        table only, limit max 200
   *   focus       graph only — a record_key to centre the neighbourhood on
   *   max_nodes   graph only, up to KG_GRAPH_MAX_NODES (200)
   *
   * Ordering is a TOTAL order (every option ends with an id tiebreaker) — do not
   * add a client-side sort on top, it will fight the cursor.
   */
  async listRecords(id, params = {}) {
    const res = await this.httpClient.get(`/kg-datasets/${encodeURIComponent(id)}/records`, {
      params,
    });
    return res.data;
  }

  /**
   * @param {string} recordKey the CUSTOMER's id — real ones contain slashes
   *   (`CIR/2026/14`), and with a composite record_key the server decides how the
   *   parts are joined. Always pass back the exact string a list response gave
   *   you; never compose one client-side.
   *
   * `text` comes from stored payloads so it includes `embed: false` fields —
   * being able to read one here is what makes "stored but not searchable"
   * different from "discarded".
   */
  async getRecord(id, recordKey) {
    const res = await this.httpClient.get(
      `/kg-datasets/${encodeURIComponent(id)}/records/${encodeURIComponent(recordKey)}`
    );
    return res.data;
  }

  /**
   * Push records. Takes the dataset **KEY**, not the id, and returns 202.
   *
   * Records and facets commit synchronously; the TEXT is only queued, and
   * embedding happens in Celery. So immediately after a push, "how many articles
   * about X?" is correct now, while "what did the article say about X?" may
   * return nothing until ingestion finishes. Watch `counts.indexed` vs
   * `counts.records` on the dataset for that.
   *
   * Max KG_PUSH_MAX_BATCH (500) records per request — the client owns the
   * chunking, and batches must be pushed SEQUENTIALLY. Concurrent pushes to one
   * dataset just contend.
   *
   * `counts.unchanged` is NOT a failure: re-pushing an identical record is a
   * deliberate no-op that does not re-embed, which is what makes a nightly full
   * sync affordable. Render it as a normal outcome.
   *
   * @param {object} [options] `signal` — an AbortSignal, so a multi-batch load
   *   can be cancelled between/during batches.
   */
  async pushRecords(key, records, { signal } = {}) {
    const res = await this.httpClient.post(
      `/kg-datasets/${encodeURIComponent(key)}/records`,
      { records },
      { timeout: PUSH_TIMEOUT_MS, signal }
    );
    return res.data;
  }

  // ── Jobs ─────────────────────────────────────────────────────────────────

  /**
   * Recent ingest jobs — an AUDIT TRAIL, not a progress feed. The row is created
   * and completed inside the same request that returns the 202, so a poller
   * never observes any other status, and a push that fails rolls the row back
   * entirely so it never appears at all. DO NOT POLL THIS.
   *
   * The `report` is the same shape as the dry-run report and is the answer to
   * "why does this dataset look wrong". For live ingestion progress use
   * `counts.indexed` vs `counts.records` on the dataset instead.
   */
  async listJobs(id, params = { limit: 20 }) {
    const res = await this.httpClient.get(`/kg-datasets/${encodeURIComponent(id)}/jobs`, {
      params,
    });
    return res.data;
  }
}

const kgDatasetService = new KgDatasetService();

export default kgDatasetService;
